import { AtomParams, AtomService } from 'src/app/core/atom.service';
import { Activity } from 'src/app/core/resources/activity.resource';
import { BillingChannel } from 'src/app/core/resources/billing-channel.resource';
import { BillingKinematic } from 'src/app/core/resources/billing-kinematic.resource';
import { BillingMode } from 'src/app/core/resources/billing-mode.resource';
import { BusinessUnit } from 'src/app/core/resources/business-unit.resource';
import { Country } from 'src/app/core/resources/country.resource';
import { Firm } from 'src/app/core/resources/firm.resource';
import { Job } from 'src/app/core/resources/job.resource';
import { NetworkChannel } from 'src/app/core/resources/network-channel.resource';
import { Offer } from 'src/app/core/resources/offer.resource';
import { OrderChannel } from 'src/app/core/resources/order-channel.resource';
import { Period } from 'src/app/core/resources/period.resource';
import { Platform } from 'src/app/core/resources/platform.resource';
import { Product } from 'src/app/core/resources/product.resource';
import { Thematic } from 'src/app/core/resources/thematic.resource';
import { Service } from 'src/app/core/resources/service.resource';
import {
  BooleanFilter, EnumListFilter,
  Filter,
  ListFilter,
  NumberFilter,
  ResourceListFilter,
  StringFilter,
} from 'src/app/filter/filter';
import { AcquisitionSources } from 'src/app/core/resources/acquisition-source.resource';
import { Currency } from 'src/app/core/resources/currency.resource';
import { Users } from 'src/app/core/resources/user.resource';
import { AcquisitionAccounts } from 'src/app/core/resources/acquisition-account.resource';
import { TrafficManager } from 'src/app/core/resources/traffic-manager.resource';
import { Lang } from 'src/app/core/resources/lang.resource';
import { Pack } from 'src/app/core/resources/pack.resource';
import { BillingPolicyLogic } from 'src/app/core/resources/billing-policy-logic';
import { BillingPolicyAction } from 'src/app/core/resources/billing-policy-action';
import { Shortcode } from 'src/app/core/resources/shortcode.ressource';
import { Cascade } from 'src/app/core/resources/cascade.resource';
import { ContractType } from 'src/app/core/resources/contract-type.resource';
import { MobileNetworkOperator } from 'src/app/core/resources/mobile-network-operator.resource';
import { PaymentMethods } from 'src/app/core/resources/payment-methods.resource';
import { InternetServiceProvider } from 'src/app/core/resources/internet-service-provider.resource';
import { Contract } from 'src/app/core/resources/contract.resource';
import { Tag } from 'src/app/core/resources/tag.resource';
import { Trademark } from 'src/app/core/resources/trademark.resource';
import { Catalog } from 'src/app/core/resources/catalog.resource';
import { CreditSetup } from 'src/app/core/resources/credit-setup.resource';
import { SKU } from 'src/app/core/resources/sku.resource';
import { BillingPolicy } from 'src/app/core/resources/billing-policy';
import { OfferBillingPolicy } from 'src/app/core/resources/offer-billing-policy';
import { LegalDocument } from '../../../core/resources/legal-document.resource';

export interface ConfigData {
  resource: any;
  include: string[];
  filters: Filter[];
  list: boolean;
  title: string;
  display: {
    path: string[];
    class?: string;
  }[];
  filtersSetted: AtomParams['filter'] | null;
  multiple?: boolean;
  notEditable?: boolean;
}

export class CountryConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: Country,
    include: ['defaultcurrency', 'defaultlang'],
    filters: [
      new StringFilter('id', 'ID', true),
      new StringFilter('name', 'Name'),
      new StringFilter('continent', 'Continent'),
      new StringFilter('currencies.id', 'Currency ID'),
      new StringFilter('currencies.name', 'Currency NAME'),
      new ResourceListFilter(
        this.atom,
        BusinessUnit,
        'businessunits.id',
        'Business unit'
      ),
      new StringFilter('langs.id', 'Language ID'),
    ],
    list: false,
    title: 'Country',
    display: [{ path: ['name'] }, { path: ['id'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class LangConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: Lang,
    include: ['countries'],
    filters: [
      new StringFilter('id', 'ID', true),
      new StringFilter('name', 'Name'),
      new StringFilter('countries.id', 'Country ID'),
    ],
    list: false,
    title: 'Lang',
    display: [{ path: ['name'] }, { path: ['id'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class PlatformConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: Platform,
    include: ['job.firm.headquarterscountry', 'job.activity', 'customfields'],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name', true),
      new StringFilter('job.firm.name', 'Firm name'),
      new NumberFilter('job.firm.id', 'Firm ID'),
      new NumberFilter('job.id', 'Job ID'),
      new StringFilter('job.firm.headquarterscountry.id', 'Country ID'),
      new StringFilter('job.firm.headquarterscountry.name', 'Country Name'),
      new NumberFilter(
        'platformcapabilities.enduserproviderjob.id',
        'Platform Capability'
      ),
      new NumberFilter(
        'platformcapabilities.enduserproviderjob.firm.headquarterscountry.id',
        'Country ID'
      ),
      new BooleanFilter('otp_identification', 'OTP Identification'),
      new BooleanFilter('header_enrichment', 'Header Enrichment'),
      new BooleanFilter('msisdn_conversion', 'MSISDN Conversion'),
      new BooleanFilter('fraud', 'Fraud Detection'),
      new ResourceListFilter(
        this.atom,
        Activity,
        'job.activity.id',
        'Activity'
      ),
    ],
    list: false,
    title: 'Platform',
    display: [
      { path: ['name'] },
      { path: ['job.firm.officialName'], class: 'secondary' },
    ],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class EndUserProviderJobConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: Job,
    include: ['firm', 'firm.headquarterscountry', 'activity'],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('firm.name', 'Firm name', true),
      new StringFilter('firm.headquarterscountry.id', 'Country ID'),
      new StringFilter('firm.headquarterscountry.name', 'Country Name'),
      new NumberFilter('platformcapabilities.platform.id', 'Platform ID'),
      new ResourceListFilter(this.atom, Activity, 'activity.id', 'Activity'),
    ],
    list: false,
    title: 'End User Provider Job',
    display: [
      { path: ['firm.officialName', 'firm.headquarterscountry.id'] },
      {
        path: ['activity.name'],

        class: 'secondary',
      },
    ],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class FirmConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: Firm,
    include: ['headquarterscountry', 'parentfirm'],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name', true),
      new StringFilter('headquarterscountry.id', 'Country ID'),
      new StringFilter('headquarterscountry.name', 'Country name'),
      new NumberFilter('parentfirm.id', 'Parent firm ID'),
      new StringFilter('parentfirm.name', 'Parent firm name'),
      new ResourceListFilter(
        this.atom,
        Activity,
        'jobs.activity.id',
        'Activities'
      ),
    ],
    list: false,
    title: 'Firm',
    display: [
      { path: ['name'] },
      {
        path: ['headquarterscountry.name'],

        class: 'secondary',
      },
    ],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class ProductConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: Product,
    include: ['country', 'service', 'handlerfirm'],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name', true),
      new StringFilter('country.id', 'Country ID'),
      new StringFilter('country.name', 'Country name'),
      new NumberFilter('service.id', 'Service ID'),
      new StringFilter('service.name', 'Service name'),
      new StringFilter('contentrate.name', 'Content Rate'),
      new ResourceListFilter(this.atom, Thematic, 'thematic', 'Thematic'),
      new ResourceListFilter(
        this.atom,
        BusinessUnit,
        'service.businessunits.id',
        'Business unit'
      ),
      new NumberFilter('service.handlerfirm.id', 'Handler firm ID'),
      new StringFilter('service.handlerfirm.name', 'Handler firm name'),
      new NumberFilter('service.legalfirm.id', 'Legal firm ID'),
      new StringFilter('service.legalfirm.name', 'Legal firm name'),
      new StringFilter('eventplatform.name', 'Event Platform'),
      new NumberFilter('service.ownerfirm.id', 'Owner firm ID'),
      new StringFilter('service.ownerfirm.name', 'Owner firm name'),
    ],
    list: false,
    title: 'Product',
    display: [
      { path: ['name'] },
      { path: ['country.name'], class: 'secondary' },
    ],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class FirmJobConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: Job,
    include: ['firm.headquarterscountry', 'activity'],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('firm.headquarterscountry.id', 'Country ID'),
      new StringFilter('firm.headquarterscountry.name', 'Country name'),
      new StringFilter('firm.name', 'Firm name', true),
      new NumberFilter('firm.id', 'Firm ID'),
      new NumberFilter(
        'platforms.platformcapabilities.enduserproviderjob.id',
        'Enduser Provider Job ID'
      ),
      new ResourceListFilter(this.atom, Activity, 'activity.id', 'Activity'),
    ],
    list: false,
    title: 'Firm Job',
    display: [
      { path: ['firm.name'] },
      { path: ['activity.name'], class: 'secondary' },
    ],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class TrademarksConfig {
  constructor(public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: Trademark,
    include: ['firm', 'products.country'],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name', true),
      new StringFilter('didomi_consent_id', 'Didomi ID'),
      new StringFilter('firm.name', 'Firm name'),
      new NumberFilter('firm.id', 'Firm ID'),
      new StringFilter('product.name', 'Product name'),
      new NumberFilter('product.id', 'Product ID'),
      new StringFilter('product.country.id', 'Country ID'),
    ],
    list: false,
    title: 'Trademark',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class CreditsetupConfig {
  constructor(public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: CreditSetup,
    include: [],
    filters: [
      new NumberFilter('id', 'ID', true),
      new StringFilter('name', 'Name'),
      new NumberFilter('offers.id', 'Offer ID'),
      new StringFilter('offers.name', 'Offer NAME'),
    ],
    list: false,
    title: 'Credit Setup',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class TagConfig {
  constructor(public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: Tag,
    include: ['messages'],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name', true),
    ],
    list: false,
    title: 'Tags',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class BusinessunitConfig {
  constructor(public multiple: boolean = false) {}

  public readonly config: ConfigData = {
    resource: BusinessUnit,
    include: [],
    filters: [],
    list: true,
    title: 'Business Unit',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class ThematicConfig {
  constructor(public multiple: boolean = false) {}

  public readonly config: ConfigData = {
    resource: Thematic,
    include: [],
    filters: [],
    list: true,
    title: 'Thematic',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class CascadeConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: Cascade,
    include: [
      'country',
      'cascadelevels.fee',
      'cascadelevels.cascadelevels.fee',
    ],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name'),
      new StringFilter('country.id', 'Country ID'),
      new StringFilter('country.name', 'Country Name'),
      new StringFilter('paymentdimensions.contract.name', 'Contracts Name'),
      new StringFilter(
        'paymentdimensions.enduserproviderjob.firm.name',
        'Contracts End User Provider'
      ),
      new StringFilter('cascadelevels.fee.name', 'Fee Name'),
      new StringFilter('cascadelevels.fee.firm.name', 'Fee Firm'),
    ],
    list: false,
    title: 'Cascades',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class ServiceConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: Service,
    include: ['ownerfirm.headquarterscountry', 'businessunits'],
    filters: [
      new NumberFilter('id', 'ID', true),
      new StringFilter('name', 'Name'),
      new ResourceListFilter(
        this.atom,
        BusinessUnit,
        'businessunits.id',
        'Business unit'
      ),
      new NumberFilter('ownerfirm.id', 'Owner Firm ID'),
      new StringFilter('ownerfirm.name', 'Owner Firm Name'),
    ],
    list: false,
    title: 'Service',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class OfferConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}

  public readonly config: ConfigData = {
    resource: Offer,
    include: ['country'],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name', true),
      new StringFilter('country.id', 'Country ID'),
      new StringFilter('country.name', 'Country name'),
      new NumberFilter('product.id', 'Product ID'),
      new StringFilter('product.name', 'Product name'),
      new NumberFilter('product.service.id', 'Service ID'),
      new StringFilter('product.service.name', 'Service name'),
      new StringFilter('currency.id', 'Currency ID'),
      new StringFilter('currency.name', 'Currency name'),
      new NumberFilter('price', 'Price'),
      new ResourceListFilter(
        this.atom,
        Period,
        'recurringperiod.id',
        'Recurring period'
      ),
      new ResourceListFilter(
        this.atom,
        BillingMode,
        'billingmode.id',
        'Billing mode'
      ),
      new ResourceListFilter(
        this.atom,
        BillingChannel,
        'billingchannel.id',
        'Billing channel'
      ),
      new StringFilter('billingproviderjob.firm.name', 'Billing Provider'),
      new NumberFilter('enduserproviderjob.id', 'End User Provider ID'),
      new StringFilter(
        'enduserproviderjob.firm.name',
        'End User Provider Firm Name'
      ),
      new NumberFilter('platform.id', 'Platform ID'),
      new StringFilter('platform.name', 'Platform name'),
      new ResourceListFilter(
        this.atom,
        BusinessUnit,
        'businessunits.id',
        'Business unit'
      ),
      new StringFilter('offercapabilities.sku.external_id', 'SKU external ID'),
      new NumberFilter('packoffers.pack.id', 'Pack ID'),
      new StringFilter('packoffers.pack.name', 'Pack name'),
      new NumberFilter(
        'offerbillingpolicy.billingpolicy.id',
        'Billing policy ID'
      ),
      new StringFilter(
        'offerbillingpolicy.billingpolicy.name',
        'Billing policy name'
      ),
      new NumberFilter('product.service.ownerfirm.id', 'Owner Firm ID'),
      new StringFilter('product.service.ownerfirm.name', 'Owner Firm name'),
    ],
    list: false,
    title: 'Offer',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class OfferBillingPolicyConfig {
  constructor(public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: OfferBillingPolicy,
    include: ['offer', 'billingpolicy'],
    filters: [
      new StringFilter('id', 'ID', true),
      new StringFilter('offer.name', 'Offer Name'),
      new NumberFilter('offer.id', 'Offer ID'),
      new NumberFilter('billingpolicy.id', 'Billing Policy ID'),
    ],
    list: false,
    title: 'Offer Billing Policy',
    display: [
      {path: ['billingpolicy.id', 'billingpolicy.name']},
      {path: ['offer.id', 'offer.name']},
    ],
    filtersSetted: null,
    multiple: this.multiple,
  }
}

export class CurrencyConfig {
  constructor(public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: Currency,
    include: ['countries'],
    filters: [
      new StringFilter('id', 'ID', true),
      new StringFilter('name', 'Name'),
      new StringFilter('countries.name', 'Country Name'),
      new StringFilter('countries.id', 'Country ID'),
    ],
    list: false,
    title: 'Currency',
    display: [{ path: ['name'] }, { path: ['id'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class CatalogConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: Catalog,
    include: [
      'contracts.businessunits',
      'businessunits',
      'paymentdimensions.platform',
    ],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name'),
      new ResourceListFilter(
        this.atom,
        BusinessUnit,
        'businessunits.id',
        'Business unit'
      ),
      new NumberFilter('contracts.id', 'Contract ID'),
      new StringFilter('contracts.name', 'Contract name'),
      new StringFilter(
        'contracts.paymentdimensions.billingproviderjob.firm.name',
        'Billing Provider'
      ),
      new StringFilter(
        'contracts.paymentdimensions.payoutproviderjob.firm.name',
        'Payout Provider'
      ),
      new StringFilter(
        'contracts.paymentdimensions.enduserproviderjob.firm.name',
        'End User Provider'
      ),
      new StringFilter(
        'contracts.paymentdimensions.country.id',
        'Country ID',
        true
      ),
      new StringFilter(
        'contracts.paymentdimensions.country.name',
        'Country name'
      ),
      new NumberFilter(
        'contracts.paymentdimensions.platform.id',
        'Platform ID'
      ),
      new StringFilter(
        'contracts.paymentdimensions.platform.name',
        'Platform name'
      ),
      new StringFilter(
        'contracts.paymentdimensions.cascade.name',
        'Cascade name'
      ),
      new NumberFilter('skus.id', 'SKU ID'),
      new StringFilter('skus.name', 'SKU name'),
      new StringFilter('skus.external_id', 'SKU external ID'),
    ],
    list: false,
    title: 'Catalog',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class ContractConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: Contract,
    include: [
      'contracttype',
      'businessunits',
      'paymentdimensions.platform.customfields',
    ],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name', true),
      new ResourceListFilter(
        this.atom,
        BusinessUnit,
        'businessunits.id',
        'Business unit'
      ),
      new NumberFilter('firms.id', 'Firm ID'),
      new StringFilter('firms.name', 'Firm name'),
      new StringFilter(
        'paymentdimensions.billingproviderjob.firm.name',
        'Billing Provider'
      ),
      new StringFilter(
        'paymentdimensions.payoutproviderjob.firm.name',
        'Payout Provider'
      ),
      new StringFilter(
        'paymentdimensions.enduserproviderjob.firm.name',
        'End User Provider'
      ),
      new StringFilter('paymentdimensions.country.id', 'Country ID'),
      new StringFilter('paymentdimensions.country.name', 'Country name'),

      new NumberFilter('paymentdimensions.platform.id', 'Platform ID'),
      new StringFilter('paymentdimensions.platform.name', 'Platform name'),
      new StringFilter('paymentdimensions.cascade.name', 'Cascade name'),
      new NumberFilter('catalogs.id', 'Catalog ID'),
      new StringFilter('catalogs.name', 'Catalog name'),
      new NumberFilter('catalogs.skus.id', 'SKU ID'),
      new StringFilter('catalogs.skus.name', 'SKU name'),
      new StringFilter('catalogs.skus.external_id', 'SKU external ID'),
    ],
    list: false,
    title: 'Contract',
    display: [
      { path: ['name'] },
      { path: ['contracttype.name'], class: 'secondary' },
    ],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class SKUConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: SKU,
    include: [
      'catalog.businessunits',
      'businessunits',
      'paymentdimensions.platform.customfields',
      'currency',
    ],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name'),
      new StringFilter('external_id', 'External ID', true),
      new ResourceListFilter(
        this.atom,
        BusinessUnit,
        'businessunits.id',
        'Business unit'
      ),
      new StringFilter(
        'catalog.contracts.paymentdimensions.billingproviderjob.firm.name',
        'Billing Provider'
      ),
      new StringFilter(
        'catalog.contracts.paymentdimensions.payoutproviderjob.firm.name',
        'Payout Provider'
      ),
      new StringFilter(
        'catalog.contracts.paymentdimensions.enduserproviderjob.firm.name',
        'End User Provider'
      ),
      new StringFilter(
        'catalog.contracts.paymentdimensions.country.id',
        'Country ID'
      ),
      new StringFilter(
        'catalog.contracts.paymentdimensions.country.name',
        'Country name'
      ),
      new NumberFilter(
        'catalog.contracts.paymentdimensions.platform.id',
        'Platform ID'
      ),
      new StringFilter(
        'catalog.contracts.paymentdimensions.platform.name',
        'Platform name'
      ),
      new StringFilter(
        'catalog.contracts.paymentdimensions.cascade.name',
        'Cascade name'
      ),
      new NumberFilter('catalog.id', 'Catalog ID'),
      new StringFilter('catalog.name', 'Catalog name'),
      new NumberFilter('catalog.contracts.id', 'Contract ID'),
      new StringFilter('catalog.contracts.name', 'Contract name'),
    ],
    list: false,
    title: 'SKU',
    display: [
      { path: ['name'] },
      // { path: ['contracttype.name'], class: 'secondary' },
    ],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class MnoConfig {
  constructor(public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: MobileNetworkOperator,
    include: ['country', 'job'],
    filters: [
      new StringFilter('id', 'ID'),
      new StringFilter('name', 'Name'),
      new StringFilter('country.name', 'Country Name'),
      new StringFilter('country.id', 'Country ID'),
      new NumberFilter('job.id', 'Job ID'),
      new StringFilter('mnc', 'MNC', true),
    ],
    list: false,
    title: 'MNO',
    display: [{ path: ['name'] }, { path: ['mcc', 'mnc'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class IspConfig {
  constructor(public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: InternetServiceProvider,
    include: ['country', 'job'],
    filters: [
      new StringFilter('id', 'ID'),
      new StringFilter('name', 'Name'),
      new StringFilter('country.name', 'Country Name'),
      new StringFilter('country.id', 'Country ID'),
      new NumberFilter('job.id', 'Job ID'),
      new NumberFilter('asn', 'ASN', true),
    ],
    list: false,
    title: 'ISP',
    display: [{ path: ['name'] }, { path: ['asn'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class PaymentMethodConfig {
  constructor(public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: PaymentMethods,
    include: ['job'],
    filters: [
      new StringFilter('id', 'ID'),
      new StringFilter('name', 'Name'),
      new NumberFilter('job.id', 'Job ID'),
      new NumberFilter('method', 'Method', true),
    ],
    list: false,
    title: 'Payment Method',
    display: [{ path: ['name'] }, { path: ['method'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class UsersConfig {
  constructor(public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: Users,
    include: ['trafficmanagers'],
    filters: [
      new StringFilter('id', 'ID'),
      new StringFilter('name', 'Name', true),
      new BooleanFilter('api', 'Is API'),
    ],
    list: false,
    title: 'Users',
    display: [{ path: ['name'] }, { path: ['id'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class TrafficManagerConfig {
  constructor(public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: TrafficManager,
    include: [
      'user',
      'acquisitionmappings.acquisitionsource',
      'acquisitionmappings.acquisitionaccount',
    ],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name', true),
    ],
    list: false,
    title: 'Traffic Manager',
    display: [{ path: ['name'] }, { path: ['id'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class AcquisitionSourceConfig {
  constructor(public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: AcquisitionSources,
    include: [],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name', true),
      new StringFilter('acquisitionaccounts.name', 'Account Name'),
      new NumberFilter('acquisitionaccounts.id', 'Account ID'),
      new NumberFilter('acquisitionmappings.id', 'Mapping ID'),
    ],
    list: false,
    title: 'Acquisition Source',
    display: [{ path: ['name'] }, { path: ['id'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class AcquisitionAccountConfig {
  constructor(public multiple: boolean = false) {}
  readonly config: ConfigData = {
    resource: AcquisitionAccounts,
    include: [
      'acquisitionsource.acquisitionmappings',
      'acquisitionmappings.acquisitionsource',
      'acquisitionmappings.acquisitionaccount',
      'acquisitionmappings.trafficmanager',
      'country',
      'currency',
      'product',
    ],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name'),
      new StringFilter('account_id', 'Account ID', true),
      new StringFilter('acquisitionsource.name', 'Source Name'),
      new NumberFilter('acquisitionsource.id', 'Source ID'),
      new StringFilter('country.id', 'Country ID'),
      new StringFilter('country.name', 'Country Name'),
      new StringFilter('currency.id', 'Currency ID'),
      new StringFilter('currency.name', 'Currency Name'),
      new NumberFilter('product.id', 'Product ID'),
      new StringFilter('product.name', 'Product Name'),
      new NumberFilter('acquisitionmappings.id', 'Mapping ID'),
    ],
    list: false,
    title: 'Acquisition Account',
    display: [{ path: ['name'] }, { path: ['account'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class BillingKinematicConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: BillingKinematic,
    include: [],
    filters: [],
    list: true,
    title: 'Billing Kinematic',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class BillingPolicyConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: BillingPolicy,
    include: [
      'logic',
      'startpolicyaction',
      'resetstartaction',
      'newcycleaction',
      'endpolicyaction',
      'newmonthaction',
      'orderedsequencerules.currency',
      'pricepointrules.currency',
      'pricepointrules.onsuccess',
      'pricepointrules.nocredit',
      'country',
      'platform',
      'period',
      'enduserproviderjob.firm.headquarterscountry',
      'enduserproviderjob.activity',
    ],
    filters: [
        new NumberFilter('id', 'ID'),
        new StringFilter('name', 'Name'),
        new StringFilter('country.id', 'Country ID', true),
        new StringFilter('country.name', 'Country Name'),
        new StringFilter('offerbillingpolicy.offer.name', 'Offer Name'),
        new BooleanFilter('split_billing', 'Split Billing'),
        new ListFilter(
          [
            { value: 'realtime', label: 'Realtime' },
            { value: 'windows', label: 'Windows' },
          ],
          'mode',
          'Mode'
        ),
        new NumberFilter('enduserproviderjob.id', 'Enduser Provider Job ID'),
        new NumberFilter('enduserproviderjob.name', 'Enduser Provider Job name'),
        new NumberFilter('platform.id', 'Platform ID'),
        new StringFilter('platform.name', 'Platform Name'),],
    list: false,
    title: 'Billing Policy',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class BillingPolicyActionConfig {
  constructor(public multiple: boolean = false, public title: string) {}
  public readonly config: ConfigData = {
    resource: BillingPolicyAction,
    include: [],
    filters: [],
    list: true,
    title: this.title || 'Billing Policy',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class BillingChannelConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: BillingChannel,
    include: [],
    filters: [],
    list: false,
    title: 'Billing Channel',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class BillingModeConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: BillingMode,
    include: [],
    filters: [],
    list: true,
    title: 'Billing Mode',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class PeriodConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: Period,
    include: [],
    filters: [],
    list: true,
    title: 'Period',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class BillingPolicyLogicConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: BillingPolicyLogic,
    include: [],
    filters: [],
    list: true,
    title: 'Billing Policy Logic',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class ContractTypeConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: ContractType,
    include: [],
    filters: [],
    list: true,
    title: 'Contract Type',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class OrderChannelConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: OrderChannel,
    include: [],
    filters: [],
    list: true,
    title: 'Order Channel',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class NetworkChannelConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: NetworkChannel,
    include: [],
    filters: [],
    list: true,
    title: 'Network Channel',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class ModeConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: NetworkChannel,
    include: [],
    filters: [],
    list: true,
    title: 'Network Channel',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class ActivityConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: Activity,
    include: [],
    filters: [],
    list: false,
    title: 'Activity',
    display: [{ path: ['name'] }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class PackConfig {
  constructor(public multiple: boolean = false) {}
  public readonly config: ConfigData = {
    resource: Pack,
    include: [],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name'),
      new StringFilter('packoffers.offer.name', 'Offer Name'),
      new NumberFilter('packoffers.offer.id', 'Offer ID'),
      new StringFilter('packoffers.offer.country.id', 'Offer Country ID'),
      new StringFilter('packoffers.offer.country.name', 'Offer Country Name'),
      new StringFilter(
        'packoffers.offer.enduserproviderjob.firm.name',
        'Offer End User Provider'
      ),
      new StringFilter(
        'packoffers.offer.billingproviderjob.firm.name',
        'Offer Billing Provider'
      ),
      new NumberFilter('packoffers.offer.platform.id', 'Offer Platform ID'),
      new StringFilter('packoffers.offer.platform.name', 'Offer Platform Name'),
      new NumberFilter(
        'packoffers.offer.product.service.ownerfirm.id',
        'Owner firm ID'
      ),
      new StringFilter(
        'packoffers.offer.product.service.ownerfirm.name',
        'Owner firm name'
      ),
    ],
    list: false,
    title: 'Pack',
    display: [{ path: ['name'] }, { path: ['id'], class: 'secondary' }],
    filtersSetted: null,
    multiple: this.multiple,
  };
}

export class ShortcodeConfig {
  constructor(public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: Shortcode,
    include: [
      'enduserproviderjob.firm.headquarterscountry',
      'country',
      'ownerfirm',
      'products',
      'keywords',
    ],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('shortcode', 'Shortcode', true),
      new StringFilter('enduserproviderjob.firm.name', 'Enduser Provider Name'),
      new StringFilter('country.name', 'Country Name'),
      new StringFilter('country.id', 'Country ID'),
      new StringFilter('ownerfirm.name', 'Owner Firm Name'),
      new NumberFilter('ownerfirm.id', 'Owner Firm ID'),
      new StringFilter('products.name', 'Product Name'),
      new NumberFilter('products.id', 'Product ID'),
      new StringFilter('keywords.keyword', 'Keyword'),
    ],
    list: false,
    title: 'Shortcode',
    display: [
      { path: ['shortcode'] },
      {
        path: [
          'enduserproviderjob.firm.headquarterscountry.id',
          'enduserproviderjob.firm.name',
        ],
        class: 'secondary',
      },
    ],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
export class LegalDocumentConfig {
  constructor(private atom: AtomService, public multiple: boolean = false) {}

  readonly config: ConfigData = {
    resource: LegalDocument,
    include: ['lang', 'country','legalfirm','products'],
    filters: [
      new NumberFilter('id', 'ID'),
      new StringFilter('name', 'Name',true),
      new EnumListFilter(this.atom, 'legaldocuments', 'document_type', 'document_type','document Type'),
      new BooleanFilter('useOnlyLastVersion',"Only Last Version"),
      new StringFilter('lang.id', 'Language ID'),
      new StringFilter('lang.name', 'Language  name'),
      new StringFilter('country.id', 'Country ID'),
      new StringFilter('country.name', 'Country name'),
      new NumberFilter('legalfirm.id', 'Handler firm ID'),
      new StringFilter('legalfirm.name', 'Handler firm name'),
      new NumberFilter('products.id', 'Products ID'),
      new StringFilter('products.name', 'Products name'),
      new NumberFilter('versions.id', 'Version ID'),
    ],
    list: false,
    title: 'Legal Document',
    display: [
      { path: ['name'] },
      {
        path: ['documentType'],
        class: 'secondary',
      },
    ],
    filtersSetted: null,
    multiple: this.multiple,
  };
}
